import { create } from 'zustand';
import config from '@/lib/config';
import { getFreshAccessToken } from '@/lib/authToken';
import { fetchWithTimeout, reasonOf } from '@/lib/http';
import { supabase } from '@/lib/supabase';
import { writePref } from '@/lib/storage';
import { queryClient } from '@/lib/queryClient';
import { useChatwootStore } from '@/store/chatwoot';
import { ACTIVE_PROJECT_KEY } from '@/store/project';

/**
 * Организации человека и переключение между ними — тем же способом, что в
 * оболочке GiperUI (GiperUI/src/store/orgStore.ts → switchOrg):
 *
 *   1. POST {TEAM}/api/organizations/:id/activate — ГиперТим проверяет, что
 *      человек состоит в организации, и записывает её в учётную запись
 *      (auth.users.raw_app_meta_data.tenant_id). Особых прав у клиента не
 *      нужно: хватает обычного пропуска, права у самого сервера
 *      (GiperTEAM/server/src/routes/organizations.ts).
 *   2. supabase.auth.refreshSession() — новый пропуск, в котором уже новая
 *      организация (app_metadata.tenant_id). Все службы UNIA (ГиперТим —
 *      список проектов, CRM, Chatwoot-вход) смотрят именно туда. Без этого
 *      шага переключение есть только на сервере.
 *   3. Проверяем, что в новом пропуске та организация, которую выбрали.
 *   4. Сбрасываем всё, что принадлежало прежней организации (активный проект,
 *      пропуск Chatwoot, кеш запросов), и перезапускаем приложение с
 *      «Входящих» — живые соединения (Chatwoot, поток CRM) поднимутся заново
 *      уже в новой организации. Перезапуск, а не «тихая» подмена — как в
 *      GiperUI: так не остаётся ни одного кеша прежней организации.
 *
 * Список — GET {TEAM}/api/organizations/my (только активные членства).
 */
export interface Org {
  id: string;
  name: string;
  accountType?: string;
}

/** Организация из пропуска (подпись проверяет сервер; здесь только читаем). */
export function tenantFromToken(token: string | null | undefined): string | null {
  if (!token) return null;
  try {
    const part = token.split('.')[1];
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '='));
    const p = JSON.parse(json) as { app_metadata?: { tenant_id?: string }; tenant_id?: string };
    return p.app_metadata?.tenant_id || p.tenant_id || null;
  } catch {
    return null;
  }
}

type Статус = 'idle' | 'loading' | 'ready' | 'error';

interface OrgState {
  orgs: Org[];
  activeOrgId: string | null;
  status: Статус;
  switchingTo: string | null;
  switchError: string | null;
  load: () => Promise<void>;
  switchOrg: (orgId: string) => Promise<void>;
  reset: () => void;
}

/** Перезапуск приложения — отдельно, чтобы подменять в тестах. */
export const _reload = { to: (path: string) => window.location.replace(path) };

export const useOrgStore = create<OrgState>((set, get) => ({
  orgs: [],
  activeOrgId: null,
  status: 'idle',
  switchingTo: null,
  switchError: null,

  load: async () => {
    if (get().status === 'loading') return;
    set({ status: 'loading' });
    try {
      const token = await getFreshAccessToken();
      if (!token) throw new Error('Нет действующего входа');
      const res = await fetchWithTimeout(`${config.TEAM_API_URL}/api/organizations/my`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await reasonOf(res));
      const body: unknown = await res.json();
      if (!Array.isArray(body)) throw new Error('Сервер вернул список организаций в неожиданном виде');
      const orgs = (body as Org[]).map((o) => ({ id: o.id, name: o.name, accountType: o.accountType }));
      set({ orgs, activeOrgId: tenantFromToken(token), status: 'ready' });
    } catch {
      // Список организаций — не главное: без него просто не показываем переключатель.
      set({ status: 'error' });
    }
  },

  switchOrg: async (orgId) => {
    const { orgs, activeOrgId, switchingTo } = get();
    if (switchingTo || orgId === activeOrgId || !orgs.some((o) => o.id === orgId)) return;
    set({ switchingTo: orgId, switchError: null });
    const fail = (message: string) => set({ switchingTo: null, switchError: message });

    // Шаг 1 — сервер.
    try {
      const token = await getFreshAccessToken();
      if (!token) return fail('Нет действующего входа. Войдите заново.');
      const res = await fetchWithTimeout(`${config.TEAM_API_URL}/api/organizations/${encodeURIComponent(orgId)}/activate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        return fail(res.status === 403 ? 'Нет доступа к этой организации.' : `Не удалось переключить организацию (ошибка ${res.status}).`);
      }
    } catch {
      return fail('Сервер не ответил — организация не переключена.');
    }

    // Шаг 2 — новый пропуск с новой организацией.
    let fresh: string | null = null;
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) return fail('Не удалось обновить вход под новую организацию. Выйдите и войдите заново.');
      fresh = data.session?.access_token ?? null;
    } catch {
      return fail('Не удалось обновить вход под новую организацию. Выйдите и войдите заново.');
    }
    if (!fresh) return fail('Вход не вернул новый пропуск. Выйдите и войдите заново.');

    // Шаг 3 — в пропуске правда новая организация.
    const claimed = tenantFromToken(fresh);
    if (claimed && claimed !== orgId) {
      return fail('Организация переключена на сервере, но вход остался в прежней. Выйдите и войдите заново.');
    }

    // Шаг 4 — чистим следы прежней организации и перезапускаемся.
    await writePref(ACTIVE_PROJECT_KEY, null);
    useChatwootStore.getState().clear();
    queryClient.clear();
    set({ activeOrgId: orgId });
    _reload.to('/inbox');
  },

  reset: () => set({ orgs: [], activeOrgId: null, status: 'idle', switchingTo: null, switchError: null }),
}));
